import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import {
  AIIcon,
  Web3Icon,
  SpaceIcon,
  AgritechIcon,
  EnergyIcon,
  FintechIcon,
  InfrastructureIcon,
} from "@/components/site/SectorIcons";

// Single source of truth for portfolio sectors + their companies.
// IMPORTANT: only companies the founder has provided. Sectors with
// no companies yet show a "to be announced" page.
export const SECTORS = [
  {
    slug: "ai",
    name: "AI",
    code: "01",
    Icon: AIIcon,
    tagline: "Compute, agents and confidential inference for the AI economy.",
    description:
      "Backing the picks-and-shovels of the AI era — GPU compute, agent coordination, autonomous trading and privacy-preserving inference.",
    image:
      "https://images.unsplash.com/photo-1655720828018-edd2daec9349?q=85&w=2000&auto=format&fit=crop",
    companies: [
      {
        name: "Groq",
        domain: "groq.com",
        note: "Ultra-low-latency AI inference engine powered by purpose-built LPU silicon for production-scale LLM workloads.",
      },
      {
        name: "xAI",
        domain: "x.ai",
        note: "Frontier AI lab building general-purpose models to accelerate human scientific discovery.",
      },
      {
        name: "Lambda AI",
        domain: "lambdalabs.com",
        note: "GPU cloud and infrastructure purpose-built for training and deploying large-scale AI models.",
      },
      {
        name: "Positron AI",
        domain: "positron.ai",
        note: "Energy-efficient AI inference hardware engineered to outperform legacy GPU stacks.",
      },
    ],
  },
  {
    slug: "web3",
    name: "Web3",
    code: "02",
    Icon: Web3Icon,
    tagline: "Modular rails powering the next generation of on-chain applications.",
    description:
      "Conviction positions across the Web3 stack — modular execution, Bitcoin-native DeFi, omnichain data, on-chain social and consumer experiences.",
    image:
      "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?q=85&w=2000&auto=format&fit=crop",
    companies: [
      { name: "Mind Network", domain: "mindnetwork.xyz", note: "Fully homomorphic encryption rails — compute on data you cannot see." },
      { name: "Fuel Network", domain: "fuel.network", note: "High-throughput modular execution layer scaling Ethereum-aligned applications." },
      { name: "0xPPL", domain: "0xppl.com", note: "On-chain social network where wallets are identities and portfolios are profiles." },
      { name: "Botanix", domain: "botanixlabs.com", note: "EVM-compatible Layer-2 bringing programmable DeFi natively to Bitcoin." },
      { name: "Overworld", domain: "overworld.gg", note: "Community-owned RPG universe where ownership powers play, trade and progression." },
      { name: "Lava", domain: "lavanet.xyz", note: "Modular access layer connecting applications to chains via decentralised RPC." },
      { name: "Uncharted", domain: "uncharted.gg", note: "On-chain discovery — surfacing tokens, protocols and narratives before consensus." },
      { name: "Analog", domain: "analog.one", note: "Omnichain time-data marketplace enabling provable cross-chain event verification." },
      { name: "Bitsmiley", domain: "bitsmiley.io", note: "Native stablecoin protocol issuing $bitUSD against Bitcoin collateral." },
      { name: "DeepLink", domain: "deeplink.cloud", note: "Decentralised low-latency cloud-gaming network powered by distributed GPUs." },
      { name: "MoonBlast", domain: "moonblast.io", note: "Consumer entertainment platform bringing mass-market rewards and loyalty on-chain." },
    ],
  },
  {
    slug: "space",
    name: "Space",
    code: "03",
    Icon: SpaceIcon,
    tagline: "Backing the new commercial space economy — from orbit to atmosphere.",
    description:
      "Strategic capital into the emerging space economy — launch, satellite communications, observation and ground infrastructure.",
    image:
      "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?q=85&w=2000&auto=format&fit=crop",
    companies: [
      {
        name: "SpaceX",
        domain: "spacex.com",
        note: "Designs, manufactures and launches advanced rockets and spacecraft — pioneering reusable launch and global satellite connectivity.",
      },
    ],
  },
  {
    slug: "agritech",
    name: "Agritech",
    code: "04",
    Icon: AgritechIcon,
    tagline: "Technology making food, water and land systems more resilient and productive.",
    description:
      "Capital into the next generation of agricultural technology — precision farming, climate-resilient cultivation, soil and water intelligence.",
    image:
      "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?q=85&w=2000&auto=format&fit=crop",
    companies: [],
  },
  {
    slug: "energy",
    name: "Energy",
    code: "05",
    Icon: EnergyIcon,
    tagline: "Positions across energy infrastructure and the transition economy.",
    description:
      "Long-duration capital across the energy stack — generation, grid infrastructure and the transition to lower-carbon systems.",
    image:
      "https://images.unsplash.com/photo-1466611653911-95081537e5b7?q=85&w=2000&auto=format&fit=crop",
    companies: [],
  },
  {
    slug: "fintech",
    name: "Fintech",
    code: "06",
    Icon: FintechIcon,
    tagline: "Capital systems being rebuilt for a programmable, global economy.",
    description:
      "Strategic capital into the rails being built underneath money — payments, settlement, lending and digital-asset infrastructure.",
    image:
      "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=85&w=2000&auto=format&fit=crop",
    companies: [
      {
        name: "Qashio",
        domain: "qashio.com",
        note: "Corporate spend management and payments platform digitising expense, cards and treasury across the MENA region.",
      },
      {
        name: "Moneeflo",
        domain: "moneeflo.com",
        note: "Cross-border payments and money-movement infrastructure built for faster, lower-cost global settlement.",
      },
      {
        name: "Trafex India",
        domain: "trafex.in",
        note: "Trade-finance and cross-border transaction platform streamlining commerce for Indian enterprises.",
      },
    ],
  },
  {
    slug: "infrastructure",
    name: "Infrastructure & Mining",
    code: "07",
    Icon: InfrastructureIcon,
    tagline: "Real assets, resources and the physical backbones of growing economies.",
    description:
      "Strategic positions across the physical backbone of growth markets — resources, mining, connectivity and real assets that compound over decades.",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=85&w=2000&auto=format&fit=crop",
    companies: [
      {
        confidential: true,
        note: "A minerals exploration company focused on nephrite jade, with an established resource base in British Columbia backed by over 40 years of industry history in the region.",
      },
      {
        confidential: true,
        note: "A private critical minerals exploration firm with a greenfield portfolio across the Arabian Nubian Shield (Saudi Arabia and Ethiopia), focused on Copper, Zinc, Lead, Gold, Lithium, and Nickel.",
      },
      {
        confidential: true,
        note: "A data-driven minerals explorer targeting large-scale copper-gold and battery metal systems across Central Asia, leveraging one of the region's largest private geological datasets alongside machine learning to identify Tier 1 discovery potential.",
      },
    ],
  },
];

export const getSectorBySlug = (slug) =>
  SECTORS.find((s) => s.slug === slug);

// Tiny hook to share hover state if needed
export const useFlash = () => {
  const [v, setV] = useState(false);
  return [v, setV];
};

export { ArrowUpRight };
